import { helpWhy } from '../../help/why';
import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Checkbox, Chip, CircularProgress, IconButton, MenuItem, Paper, Table, TableBody,
    Dialog, DialogActions, DialogContent, DialogTitle, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { TnaMilestoneService } from '../../services/tnaMilestone.service';
import {
    ICreateTnaMilestoneRequest, ITnaMilestone, TNA_MILESTONE_TYPE_LABELS, TnaMilestoneStatus, TnaMilestoneType,
} from '../../interfaces/garments/tnaMilestone.interface';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { enumLabel } from '../../utils/garmentsLabels';

const MILESTONE_TYPES = Object.keys(TNA_MILESTONE_TYPE_LABELS) as TnaMilestoneType[];

const REMARKS_MAX_LENGTH = 500;
const REMARKS_TRUNCATE_LENGTH = 40;

const STATUS_COLORS: Record<TnaMilestoneStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
    COMPLETED: 'success',
    OVERDUE: 'error',
    DUE_SOON: 'warning',
    ON_TRACK: 'default',
};

interface EditDraft {
    targetDate: string;
    remarks: string;
}

const draftFromMilestone = (m: ITnaMilestone): EditDraft => ({
    targetDate: m.targetDate ?? '',
    remarks: m.remarks ?? '',
});

interface AddDraft {
    milestoneType: TnaMilestoneType | '';
    targetDate: string;
    remarks: string;
}

const emptyAddDraft: AddDraft = { milestoneType: '', targetDate: '', remarks: '' };

interface TnaCalendarPanelProps {
    buyerOrderId: string;
    canManage: boolean;
}

/**
 * T&A (time-and-action) calendar panel for a buyer order (garments-tna-calendar-order-dashboard,
 * task 4) — self-fetching off `buyerOrderId`, mirroring `MaterialConsumptionEditor`'s shape.
 * "Generate T&A calendar" idempotently back-fills the standard milestone set off the order's ship
 * date (`TnaMilestoneService#generate`); each row's status chip is server-derived
 * (`TnaMilestoneStatus`), never recomputed here.
 */
export function TnaCalendarPanel({ buyerOrderId, canManage }: TnaCalendarPanelProps) {
    const toast = useToast();

    const [milestones, setMilestones] = useState<ITnaMilestone[]>([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);

    const [adding, setAdding] = useState(false);
    const [addDraft, setAddDraft] = useState<AddDraft>(emptyAddDraft);

    const [editingId, setEditingId] = useState<string | null>(null);
    const [editDraft, setEditDraft] = useState<EditDraft>({ targetDate: '', remarks: '' });

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            const res = await TnaMilestoneService.list(institutionId, buyerOrderId);
            setMilestones([...res].sort((a, b) => a.sequence - b.sequence));
        } catch (e) {
            toast.showApiError(e, 'Failed to load T&A calendar');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyerOrderId]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyerOrderId]);

    const handleGenerate = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setGenerating(true);
        try {
            const res = await TnaMilestoneService.generate(institutionId, buyerOrderId);
            setMilestones([...res].sort((a, b) => a.sequence - b.sequence));
            toast.showSuccess('T&A calendar generated');
        } catch (e) {
            toast.showApiError(e, 'Failed to generate T&A calendar');
        } finally {
            setGenerating(false);
        }
    };

    // Marking Done asks for the actual completion date first (defaults to today, never in the future).
    const [doneTarget, setDoneTarget] = useState<ITnaMilestone | null>(null);
    const [doneDate, setDoneDate] = useState('');

    const requestToggle = (m: ITnaMilestone) => {
        if (m.completed) {
            toggleDone(m, false);
            return;
        }
        setDoneTarget(m);
        setDoneDate(toLocalIsoDate());
    };

    const doneWhy = !doneDate ? 'Enter the actual completion date.'
        : doneDate > toLocalIsoDate() ? 'The actual date cannot be in the future.'
        : null;

    const confirmDone = async () => {
        if (!doneTarget || !doneDate) return;
        const target = doneTarget;
        setDoneTarget(null);
        await toggleDone(target, true, doneDate);
    };

    const toggleDone = async (m: ITnaMilestone, nextCompleted: boolean, actualDate?: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setSaving(true);
        try {
            const updated = await TnaMilestoneService.update(institutionId, buyerOrderId, m.id, {
                targetDate: m.targetDate ?? null,
                offsetDays: m.offsetDays,
                sequence: m.sequence,
                remarks: m.remarks ?? null,
                completed: nextCompleted,
                actualDate: nextCompleted ? actualDate ?? m.actualDate ?? null : null,
            });
            setMilestones(prev => prev.map(x => (x.id === updated.id ? updated : x)).sort((a, b) => a.sequence - b.sequence));
        } catch (e) {
            toast.showApiError(e, 'Failed to update milestone');
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (m: ITnaMilestone) => {
        setEditingId(m.id);
        setEditDraft(draftFromMilestone(m));
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditDraft({ targetDate: '', remarks: '' });
    };

    const saveEdit = async (m: ITnaMilestone) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setSaving(true);
        try {
            const updated = await TnaMilestoneService.update(institutionId, buyerOrderId, m.id, {
                targetDate: editDraft.targetDate || null,
                offsetDays: m.offsetDays,
                sequence: m.sequence,
                remarks: editDraft.remarks.trim() || null,
                completed: m.completed,
                actualDate: m.actualDate ?? null,
            });
            setMilestones(prev => prev.map(x => (x.id === updated.id ? updated : x)).sort((a, b) => a.sequence - b.sequence));
            toast.showSuccess('Milestone updated');
            cancelEdit();
        } catch (e) {
            toast.showApiError(e, 'Failed to update milestone');
        } finally {
            setSaving(false);
        }
    };

    const startAdd = () => {
        setAddDraft(emptyAddDraft);
        setAdding(true);
    };

    const cancelAdd = () => {
        setAdding(false);
        setAddDraft(emptyAddDraft);
    };

    const addWhy = saving ? null
        : !addDraft.milestoneType ? 'Pick the milestone type first.'
        : null;

    const handleAdd = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !addDraft.milestoneType) return;
        const request: ICreateTnaMilestoneRequest = {
            milestoneType: addDraft.milestoneType,
            targetDate: addDraft.targetDate || null,
            remarks: addDraft.remarks.trim() || null,
        };
        setSaving(true);
        try {
            await TnaMilestoneService.create(institutionId, buyerOrderId, request);
            toast.showSuccess('Milestone added');
            cancelAdd();
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to add milestone');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        if (!window.confirm('Delete this milestone?')) return;
        setSaving(true);
        try {
            await TnaMilestoneService.remove(institutionId, buyerOrderId, id);
            toast.showSuccess('Milestone deleted');
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to delete milestone');
        } finally {
            setSaving(false);
        }
    };

    const colSpan = canManage ? 7 : 5;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">T&amp;A Calendar</Typography>
                {canManage && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {!adding && (
                            <Button size="small" startIcon={<AddIcon />} data-help="merchandising.tna.add" onClick={startAdd} disabled={saving}>
                                Add Milestone
                            </Button>
                        )}
                        <Button
                            size="small" variant="contained" onClick={handleGenerate}
                            disabled={generating} data-testid="generate-tna-button" data-help="merchandising.tna.generate"
                        >
                            {generating ? <CircularProgress size={16} /> : 'Generate T&A Calendar'}
                        </Button>
                    </Box>
                )}
            </Box>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} variant="outlined" data-testid="tna-milestone-table" data-help-section="merchandising.tna.table">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="merchandising.tna.col.milestone">Milestone</TableCell>
                                <TableCell data-help="merchandising.tna.col.target">Target Date</TableCell>
                                <TableCell data-help="merchandising.tna.col.actual">Actual Date</TableCell>
                                <TableCell data-help="merchandising.tna.col.status">Status</TableCell>
                                <TableCell data-help="merchandising.tna.col.remarks">Remarks</TableCell>
                                {canManage && <TableCell align="center" data-help="merchandising.tna.col.done">Done</TableCell>}
                                {canManage && <TableCell align="right" data-help="merchandising.tna.col.actions">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {milestones.length === 0 && !adding && (
                                <TableRow>
                                    <TableCell colSpan={colSpan} align="center">
                                        <Typography color="text.secondary" sx={{ py: 4 }}>
                                            No T&amp;A milestones yet.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {milestones.map(m => {
                                const isEditing = editingId === m.id;
                                return (
                                    <TableRow key={m.id} data-testid={`tna-row-${m.id}`}>
                                        <TableCell>{m.displayLabel || TNA_MILESTONE_TYPE_LABELS[m.milestoneType]}</TableCell>
                                        <TableCell sx={{ minWidth: 150 }}>
                                            {isEditing ? (
                                                <TextField
                                                    data-help="merchandising.tna.col.target" size="small" type="date" value={editDraft.targetDate}
                                                    InputLabelProps={{ shrink: true }}
                                                    onChange={e => setEditDraft(prev => ({ ...prev, targetDate: e.target.value }))}
                                                />
                                            ) : (m.targetDate ? formatDate(m.targetDate) : '—')}
                                        </TableCell>
                                        <TableCell>{m.actualDate ? formatDate(m.actualDate) : '—'}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={enumLabel(m.status)} color={STATUS_COLORS[m.status]} data-testid={`tna-status-${m.id}`} data-help="merchandising.tna.status" />
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 180 }} data-testid={`tna-remarks-${m.id}`}>
                                            {isEditing ? (
                                                <TextField
                                                    data-help="merchandising.tna.col.remarks" size="small" fullWidth multiline maxRows={3} value={editDraft.remarks}
                                                    inputProps={{ maxLength: REMARKS_MAX_LENGTH }}
                                                    placeholder="Remarks"
                                                    onChange={e => setEditDraft(prev => ({ ...prev, remarks: e.target.value }))}
                                                />
                                            ) : m.remarks ? (
                                                m.remarks.length > REMARKS_TRUNCATE_LENGTH ? (
                                                    <Tooltip title={m.remarks}>
                                                        <span>{`${m.remarks.slice(0, REMARKS_TRUNCATE_LENGTH)}…`}</span>
                                                    </Tooltip>
                                                ) : (
                                                    m.remarks
                                                )
                                            ) : '—'}
                                        </TableCell>
                                        {canManage && (
                                            <TableCell align="center">
                                                <Checkbox
                                                    data-help="merchandising.tna.done" size="small" checked={m.completed} disabled={saving}
                                                    onChange={() => requestToggle(m)}
                                                    inputProps={{ 'aria-label': `mark ${m.displayLabel} done` }}
                                                />
                                            </TableCell>
                                        )}
                                        {canManage && (
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                {isEditing ? (
                                                    <>
                                                        <IconButton data-help="merchandising.tna.saveEdit" size="small" onClick={() => saveEdit(m)} disabled={saving} aria-label="save">
                                                            <SaveIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton data-help="merchandising.tna.cancelEdit" size="small" onClick={cancelEdit} disabled={saving} aria-label="cancel edit">
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                ) : (
                                                    <>
                                                        <IconButton data-help="merchandising.tna.edit" size="small" onClick={() => startEdit(m)} disabled={saving} aria-label="edit">
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton data-help="merchandising.tna.delete" size="small" onClick={() => handleDelete(m.id)} disabled={saving} aria-label="delete">
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                            {adding && (
                                <TableRow data-testid="tna-add-row">
                                    <TableCell sx={{ minWidth: 180 }}>
                                        <TextField
                                            data-help="merchandising.tna.col.milestone" select size="small" fullWidth value={addDraft.milestoneType}
                                            onChange={e => setAddDraft(prev => ({ ...prev, milestoneType: e.target.value as TnaMilestoneType }))}
                                        >
                                            <MenuItem value="">Select type</MenuItem>
                                            {MILESTONE_TYPES.map(t => (
                                                <MenuItem key={t} value={t}>{TNA_MILESTONE_TYPE_LABELS[t]}</MenuItem>
                                            ))}
                                        </TextField>
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>
                                        <TextField
                                            data-help="merchandising.tna.col.target" size="small" type="date" value={addDraft.targetDate}
                                            InputLabelProps={{ shrink: true }}
                                            onChange={e => setAddDraft(prev => ({ ...prev, targetDate: e.target.value }))}
                                        />
                                    </TableCell>
                                    <TableCell />
                                    <TableCell />
                                    <TableCell sx={{ minWidth: 180 }}>
                                        <TextField
                                            data-help="merchandising.tna.col.remarks" size="small" fullWidth multiline maxRows={3} value={addDraft.remarks}
                                            inputProps={{ maxLength: REMARKS_MAX_LENGTH }}
                                            placeholder="Remarks"
                                            onChange={e => setAddDraft(prev => ({ ...prev, remarks: e.target.value }))}
                                        />
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <IconButton data-help="merchandising.tna.saveNew" {...helpWhy(addWhy ? { kind: 'status', message: addWhy } : null)} size="small" onClick={handleAdd} disabled={saving || !addDraft.milestoneType} aria-label="save new milestone">
                                            <SaveIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton data-help="merchandising.tna.cancelNew" size="small" onClick={cancelAdd} disabled={saving} aria-label="cancel new milestone">
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <Dialog open={!!doneTarget} onClose={() => setDoneTarget(null)} maxWidth="xs" fullWidth PaperProps={{ 'data-help-page': 'merchandising.tnaDone.page' } as any}>
                <DialogTitle>Mark milestone done</DialogTitle>
                <DialogContent data-help-section="merchandising.tnaDone.section.done">
                    <TextField
                        data-help="merchandising.tna.doneDate" label="Actual date" type="date" fullWidth margin="dense" value={doneDate}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: toLocalIsoDate() }}
                        onChange={e => setDoneDate(e.target.value)}
                        error={!!doneDate && doneDate > toLocalIsoDate()}
                        helperText="When was this milestone actually completed?"
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="merchandising.tna.doneCancel" onClick={() => setDoneTarget(null)}>Cancel</Button>
                    <Button
                        variant="contained" data-help="merchandising.tna.markDone" {...helpWhy(doneWhy ? { kind: 'status', message: doneWhy } : null)} onClick={confirmDone}
                        disabled={!doneDate || doneDate > toLocalIsoDate()}
                    >
                        Mark done
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default TnaCalendarPanel;
